import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Building2, Edit, Trash2, ArrowLeft, Users, CheckCircle, XCircle, RefreshCw, Activity, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { MerchantDetailModal } from './MerchantDetailModal';
import { WebhookList } from './WebhookList';
import { WebhookDetails } from './WebhookDetails';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription, DialogClose } from './ui/dialog';
import { Label } from './ui/label';

import { partnerApi } from '../lib/api/partnerApi';
import { merchantApi } from '../lib/api/merchantApi';
import { webhookApi } from '../lib/api/webhookApi';
import { PartnerDto, Merchant, WebhookResponse } from '../lib/api/types';

interface PartnerDetailProps {
  partnerId?: string;
  onEdit?: (partner: PartnerDto) => void;
  onBack?: () => void;
}

export const PartnerDetail: React.FC<PartnerDetailProps> = ({
  partnerId: propPartnerId,
  onEdit,
  onBack,
}) => {
  const { partnerId: paramPartnerId } = useParams<{ partnerId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const partnerId = propPartnerId || paramPartnerId;

  // State for merchant detail modal
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null);
  const [isMerchantModalOpen, setIsMerchantModalOpen] = useState(false);

  // State for webhook detail modal
  const [selectedWebhookId, setSelectedWebhookId] = useState<string | null>(null);
  const [isWebhookModalOpen, setIsWebhookModalOpen] = useState(false);

  // Query to fetch partner details
  const {
    data: partnerData,
    isLoading: isLoadingPartner,
    error: partnerError,
  } = useQuery({
    queryKey: ['partners', partnerId],
    queryFn: () => partnerApi.getPartner(partnerId!),
    enabled: !!partnerId,
  });

  // Query to fetch merchants associated with this partner
  const {
    data: merchantData,
    isLoading: isLoadingMerchants,
  } = useQuery({
    queryKey: ['merchants', 'by-partner', partnerId],
    queryFn: () => merchantApi.getAllMerchantsAdmin(partnerId!),
    enabled: !!partnerId,
  });

  // Webhooks monitoring state
  const [liveMonitoring, setLiveMonitoring] = useState(true);
  const [isSendDialogOpen, setIsSendDialogOpen] = useState(false);
  const [payloadText, setPayloadText] = useState<string>("{\"event\":\"test\",\"data\":{\"example\":true}}");
  const [sendResult, setSendResult] = useState<WebhookResponse | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  // Query to fetch webhook statistics for this partner, with optional polling
  const {
    data: webhookStats,
    isLoading: isLoadingWebhookStats,
    refetch: refetchWebhookStats,
  } = useQuery({
    queryKey: ['webhooks', 'stats', partnerId],
    queryFn: () => webhookApi.getWebhookStatistics(partnerId!),
    enabled: !!partnerId,
    refetchInterval: liveMonitoring ? 2000 : false,
  });

  // Mutations for clearing stats and sending test webhook
  const clearWebhookStatsMutation = useMutation({
    mutationFn: () => webhookApi.clearWebhookStatistics(partnerId!),
    onSuccess: () => {
      // Immediately set the statistics to zero values for instant UI feedback
      queryClient.setQueryData(['webhooks', 'stats', partnerId], {
        partnerId: partnerId!,
        totalWebhooks: 0,
        successfulWebhooks: 0,
        failedWebhooks: 0,
        lastWebhookReceived: undefined,
        averageProcessingTime: undefined,
      });

      // Invalidate and refetch to ensure consistency with server state
      queryClient.invalidateQueries({ queryKey: ['webhooks', 'stats', partnerId] });
      queryClient.invalidateQueries({ queryKey: ['webhooks', 'list', partnerId] });
    },
  });

  const sendWebhookMutation = useMutation({
    mutationFn: (payload: any) => webhookApi.sendWebhook(partnerId!, payload),
    onSuccess: (data) => {
      setSendResult(data);
      setSendError(null);
      refetchWebhookStats();
    },
    onError: (error: any) => {
      setSendError(error?.message || 'Failed to send webhook');
    },
  });

  // Delete partner mutation
  const deletePartnerMutation = useMutation({
    mutationFn: partnerApi.deletePartner,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      navigate('/partners');
    },
    onError: (error: Error) => {
      console.error('Failed to delete partner:', error);
    },
  });

  const partner = partnerData?.partner;
  const merchants = merchantData?.merchants || [];

  const handleEdit = () => {
    if (partner && onEdit) {
      onEdit(partner);
    } else if (partner) {
      navigate(`/partners/${partner.partnerId}/edit`);
    }
  };

  const handleDelete = () => {
    if (partnerId) {
      deletePartnerMutation.mutate(partnerId);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/partners');
    }
  };

  const handleViewMerchant = (merchant: Merchant) => {
    setSelectedMerchant(merchant);
    setIsMerchantModalOpen(true);
  };

  const handleCloseMerchantModal = () => {
    setIsMerchantModalOpen(false);
    setSelectedMerchant(null);
  };

  const handleViewWebhook = (webhookId: string) => {
    setSelectedWebhookId(webhookId);
    setIsWebhookModalOpen(true);
  };

  const handleCloseWebhookModal = () => {
    setIsWebhookModalOpen(false);
    setSelectedWebhookId(null);
  };

  if (isLoadingPartner) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-muted-foreground">Loading partner details...</div>
      </div>
    );
  }

  if (partnerError || !partner) {
    return (
      <div className="flex flex-col items-center justify-center p-8 space-y-4">
        <div className="text-destructive">Failed to load partner details</div>
        <Button onClick={handleBack} variant="outline">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Partners
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button onClick={handleBack} variant="outline" size="sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{partner.partnerName}</h1>
            <p className="text-muted-foreground">{partner.partnerCode}</p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Button onClick={handleEdit} variant="outline">
            <Edit className="mr-2 h-4 w-4" />
            Edit
          </Button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Partner</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete "{partner.partnerName}"?
                  {merchants.length > 0 && (
                    <span className="block mt-2 text-destructive font-medium">
                      Warning: This partner has {merchants.length} associated merchant(s).
                      Deleting this partner may affect those merchants.
                    </span>
                  )}
                  This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={deletePartnerMutation.isPending}
                >
                  {deletePartnerMutation.isPending ? 'Deleting...' : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Partner Information */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Partner Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Partner Name</label>
              <p className="text-sm">{partner.partnerName}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">Partner ID</label>
              <p className="text-sm font-mono">{partner.partnerId}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">External Partner ID</label>
              <p className="text-sm font-mono">{partner.partnerCode}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">Biller ID</label>
              <p className="text-sm font-mono">{partner.billerId}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">Terminal ID</label>
              <p className="text-sm font-mono">{partner.termId ?? 'Not provided'}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">Certificate String</label>
              <p className="text-sm break-all font-mono">{partner.certStr ?? 'Not provided'}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">Webhook Secret</label>
              <p className="text-sm break-all font-mono">{partner.webhookSecret ?? 'Not configured'}</p>
            </div>

            {partner.description && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Description</label>
                <p className="text-sm">{partner.description}</p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-muted-foreground">Status</label>
              <div className="flex items-center gap-2">
                {partner.isActive ? (
                  <>
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-green-700">Active</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4 text-red-500" />
                    <span className="text-sm text-red-700">Inactive</span>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Associated Merchants */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Associated Merchants ({merchants.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingMerchants ? (
              <div className="text-center text-muted-foreground py-4">
                Loading merchants...
              </div>
            ) : merchants.length === 0 ? (
              <div className="text-center text-muted-foreground py-4">
                No merchants associated with this partner
              </div>
            ) : (
              <div className="space-y-3">
                {merchants.slice(0, 5).map((merchant: any) => (
                  <div
                    key={merchant.id}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div>
                      <p className="font-medium">{merchant.merchantName}</p>
                      <p className="text-sm text-muted-foreground">
                        Account: {merchant.accountNumber}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        ID: {merchant.id}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewMerchant(merchant)}
                    >
                      View
                    </Button>
                  </div>
                ))}

                {merchants.length > 5 && (
                  <div className="text-center pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/merchants?partnerId=${partnerId}`)}
                    >
                      View All {merchants.length} Merchants
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Webhooks Monitoring */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Webhooks
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <input
                id="liveMonitoring"
                type="checkbox"
                checked={liveMonitoring}
                onChange={(e) => setLiveMonitoring(e.target.checked)}
              />
              <Label htmlFor="liveMonitoring">Live monitoring</Label>
              {liveMonitoring && (
                <span className="text-xs text-muted-foreground">Polling every 2s</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  refetchWebhookStats();
                  queryClient.invalidateQueries({ queryKey: ['webhooks', 'list', partnerId] });
                }}
                disabled={isLoadingWebhookStats}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => clearWebhookStatsMutation.mutate()}
                disabled={clearWebhookStatsMutation.isPending}
              >
                {clearWebhookStatsMutation.isPending ? 'Clearing...' : 'Clear'}
              </Button>
              <Dialog open={isSendDialogOpen} onOpenChange={setIsSendDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm">
                    <Send className="mr-2 h-4 w-4" />
                    Send Test Webhook
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Send Test Webhook</DialogTitle>
                    <DialogDescription>
                      Provide a JSON payload to simulate a webhook for this partner.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-2">
                    <Label htmlFor="webhookPayload">Payload (JSON)</Label>
                    <textarea
                      id="webhookPayload"
                      className="w-full rounded-md border p-2 font-mono text-sm min-h-[160px]"
                      value={payloadText}
                      onChange={(e) => setPayloadText(e.target.value)}
                    />
                    {sendError && (
                      <div className="text-destructive text-sm">{sendError}</div>
                    )}
                    {sendResult && (
                      <div className="text-xs text-muted-foreground">
                        Sent: {sendResult.webhookId} at {sendResult.timestamp} — {sendResult.status}
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline">Close</Button>
                    </DialogClose>
                    <Button
                      onClick={() => {
                        try {
                          const parsed = JSON.parse(payloadText);
                          sendWebhookMutation.mutate(parsed);
                        } catch (err) {
                          setSendError('Invalid JSON payload');
                        }
                      }}
                      disabled={sendWebhookMutation.isPending}
                    >
                      {sendWebhookMutation.isPending ? 'Sending...' : 'Send'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="p-3 rounded-md border">
              <div className="text-xs text-muted-foreground">Total</div>
              <div className="text-lg font-semibold">{webhookStats?.totalWebhooks ?? 0}</div>
            </div>
            <div className="p-3 rounded-md border">
              <div className="text-xs text-muted-foreground">Successful</div>
              <div className="text-lg font-semibold">{webhookStats?.successfulWebhooks ?? 0}</div>
            </div>
            <div className="p-3 rounded-md border">
              <div className="text-xs text-muted-foreground">Failed</div>
              <div className="text-lg font-semibold">{webhookStats?.failedWebhooks ?? 0}</div>
            </div>
            <div className="p-3 rounded-md border col-span-2 md:col-span-3">
              <div className="text-xs text-muted-foreground">Last Received</div>
              <div className="text-sm">{webhookStats?.lastWebhookReceived || '—'}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Webhook List */}
      <WebhookList
        partnerId={partnerId!}
        onWebhookSelect={handleViewWebhook}
        liveMonitoring={liveMonitoring}
      />

      {/* Merchant Detail Modal */}
      <MerchantDetailModal
        merchant={selectedMerchant}
        isOpen={isMerchantModalOpen}
        onClose={handleCloseMerchantModal}
      />

      {/* Webhook Detail Modal */}
      {selectedWebhookId && (
        <Dialog open={isWebhookModalOpen} onOpenChange={setIsWebhookModalOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Webhook Details</DialogTitle>
            </DialogHeader>
            <WebhookDetails
              partnerId={partnerId!}
              webhookId={selectedWebhookId}
              onBack={handleCloseWebhookModal}
            />
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" onClick={handleCloseWebhookModal}>
                  Close
                </Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
